import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Check, Circle, ArrowRight, Sparkles } from 'lucide-react';
import type { OnboardingStep, OnboardingProgress } from '../../hooks/useOnboarding';
import { useOnboarding } from '../../hooks/useOnboarding';
import { WelcomeModal } from './WelcomeModal';

// Step actions configuration
const STEP_ACTIONS: Record<string, { url: string; cta: string; instruction: string }> = {
  welcome: {
    url: '/',
    cta: 'Commencer',
    instruction: 'Bienvenue ! Cliquez pour découvrir Totemium.',
  },
  profile: {
    url: '/account/settings',
    cta: 'Configurer mon profil',
    instruction: 'Renseignez votre secteur d\'activité et vos cas d\'usage.',
  },
  first_agent: {
    url: '/agents',
    cta: 'Créer un agent',
    instruction: 'Créez votre premier agent IA en cliquant sur "Nouvel agent".',
  },
  first_knowledge: {
    url: '/knowledges',
    cta: 'Ajouter une connaissance',
    instruction: 'Importez un document pour enrichir votre agent.',
  },
  first_test: {
    url: '/agents',
    cta: 'Tester mon agent',
    instruction: 'Ouvrez un agent et testez-le dans le Playground.',
  },
};

interface OnboardingChecklistProps {
  progress: OnboardingProgress;
}

export function OnboardingChecklist({ 
  progress, 
}: OnboardingChecklistProps) {
  const navigate = useNavigate();
  const { completeStep } = useOnboarding();
  const [activeStep, setActiveStep] = useState<string | null>(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const handleStepClick = async (step: OnboardingStep) => {
    if (step.completed) return;
    
    const action = STEP_ACTIONS[step.id];
    if (!action) return;

    setActiveStep(step.id);

    // Special case for welcome - show welcome modal
    if (step.id === 'welcome') {
      setShowWelcomeModal(true);
      // Notify widget to hide tooltip while modal is open
      window.dispatchEvent(new CustomEvent('onboarding:modal-open'));
      return;
    }

    // Minimize widget when navigating to step pages
    window.dispatchEvent(new CustomEvent('onboarding:widget-minimize'));
    
    // Navigate to the relevant page
    navigate(action.url);
  };

  const handleWelcomeComplete = async () => {
    await completeStep('welcome');
    setShowWelcomeModal(false);
    setActiveStep(null);
    // Emit event to refresh progress in other components
    window.dispatchEvent(new CustomEvent('onboarding:step-completed'));
    // Minimize widget before navigating
    window.dispatchEvent(new CustomEvent('onboarding:widget-minimize'));
    // Navigate to next step (Profile)
    navigate(STEP_ACTIONS.profile.url);
  };

  const completedCount = progress.steps_completed.length;
  const totalCount = progress.steps.length;

  return (
    <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-lg border border-emerald-200 dark:border-emerald-800 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-emerald-50 to-white dark:from-emerald-900/20 dark:to-zinc-800">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500 text-white text-sm font-semibold">
          {completedCount}/{totalCount}
        </div>
        <div>
          <h3 className="font-semibold text-zinc-900 dark:text-white text-sm">
            Guide de démarrage
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            {progress.progress_percent}% complété
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-zinc-100 dark:bg-zinc-700">
        <div 
          className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500"
          style={{ width: `${progress.progress_percent}%` }}
        />
      </div>

      {/* Steps list */}
      <ul className="divide-y divide-zinc-100 dark:divide-zinc-700" role="list">
        {progress.steps
          .sort((a, b) => a.order - b.order)
          .map((step) => (
            <StepItem 
              key={step.id} 
              step={step} 
              isActive={activeStep === step.id}
              onClick={() => handleStepClick(step)}
            />
          ))}
      </ul>

      {/* Current step instruction */}
      {progress.current_step && !progress.steps.find(s => s.id === progress.current_step)?.completed && (
        <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-t border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              {STEP_ACTIONS[progress.current_step]?.instruction || 'Continuez votre progression !'}
            </p>
          </div>
        </div>
      )}

      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => {
          setShowWelcomeModal(false);
          setActiveStep(null);
        }}
        onStart={handleWelcomeComplete}
      />
    </div>
  );
}

interface StepItemProps {
  step: OnboardingStep;
  isActive?: boolean;
  onClick?: () => void;
}

function StepItem({ step, isActive, onClick }: StepItemProps) {
  const action = STEP_ACTIONS[step.id];
  const isClickable = !step.completed && action;

  return (
    <li 
      className={`flex items-start gap-3 px-4 py-3 transition-colors ${
        step.completed 
          ? 'bg-emerald-50/50 dark:bg-emerald-900/10' 
          : isActive
            ? 'bg-emerald-100 dark:bg-emerald-900/30'
            : isClickable
              ? 'hover:bg-zinc-50 dark:hover:bg-zinc-700/50 cursor-pointer'
              : ''
      }`}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && onClick?.() : undefined}
      data-onboarding={`step-${step.id}`}
    >
      <div className="flex-shrink-0 mt-0.5">
        {step.completed ? (
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white">
            <Check className="w-3 h-3" />
          </div>
        ) : isActive ? (
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white animate-pulse">
            <ArrowRight className="w-3 h-3" />
          </div>
        ) : (
          <Circle className="w-5 h-5 text-zinc-300 dark:text-zinc-600" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-sm font-medium ${
            step.completed 
              ? 'text-emerald-700 dark:text-emerald-400 line-through' 
              : 'text-zinc-900 dark:text-white'
          }`}>
            {step.name}
            {step.required && !step.completed && (
              <span className="ml-1.5 text-xs text-amber-500 dark:text-amber-400">
                (requis)
              </span>
            )}
          </p>
          {isClickable && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {action.cta}
              <ArrowRight className="w-3 h-3" />
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
          {step.description}
        </p>
      </div>
    </li>
  );
}

export default OnboardingChecklist;
